import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as MdIcons from 'react-icons/md';
import { format } from 'date-fns';

const { MdClose, MdDelete, MdAccessTime, MdMusicNote, MdArrowForward, MdDeleteSweep } = MdIcons;

const HistorySidebar = ({ isOpen, onClose, history, onSelect, onDelete, onClearHistory }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
          />

          {/* Sidebar */}
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-white dark:bg-surface-dark border-l border-slate-200 dark:border-border-dark shadow-2xl z-[101] flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b border-slate-200 dark:border-border-dark flex items-center justify-between bg-white/50 dark:bg-surface-dark/50 backdrop-blur-md">
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">History</h3>
                <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
                  {history.length} saved analysis{history.length !== 1 ? 'es' : ''}
                </p>
              </div>
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 transition-colors"
              >
                <SafeIcon icon={MdClose} className="text-xl" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
              {history.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-50">
                  <SafeIcon icon={MdMusicNote} className="text-6xl text-slate-300 dark:text-border-dark mb-4" />
                  <p className="text-slate-500 dark:text-text-secondary font-medium">No history yet</p>
                  <p className="text-xs text-slate-400 mt-2">Analyzed tracks will appear here.</p>
                </div>
              ) : (
                history.map((item) => (
                  <motion.div 
                    key={item.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="group bg-slate-50 dark:bg-background-dark border border-slate-200 dark:border-border-dark rounded-xl p-3 hover:border-primary dark:hover:border-primary transition-all cursor-pointer relative overflow-hidden"
                    onClick={() => onSelect(item)}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-slate-900 dark:text-white text-sm truncate pr-6">
                          {item.fileName}
                        </h4>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-text-secondary">
                          <SafeIcon icon={MdAccessTime} className="text-xs" />
                          <span>{format(new Date(item.timestamp), 'MMM d, h:mm a')}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <button 
                          onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                          className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 text-slate-400 hover:text-red-500 transition-colors z-10"
                          title="Delete"
                        >
                          <SafeIcon icon={MdDelete} className="text-lg" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex gap-2">
                      {item.results.youtubeContent?.tags?.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-white dark:bg-surface-dark rounded text-[10px] text-slate-500 border border-slate-200 dark:border-border-dark/50">
                          {tag}
                        </span>
                      ))}
                      {item.results.youtubeContent?.tags?.length > 3 && (
                        <span className="px-2 py-0.5 text-[10px] text-slate-400">
                          +{item.results.youtubeContent.tags.length - 3}
                        </span>
                      )}
                    </div>

                    <div className="absolute right-4 bottom-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <SafeIcon icon={MdArrowForward} className="text-primary text-xl" />
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer */}
            {history.length > 0 && (
              <div className="p-4 border-t border-slate-200 dark:border-border-dark bg-slate-50 dark:bg-surface-dark/30">
                <button 
                  onClick={onClearHistory}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                >
                  <SafeIcon icon={MdDeleteSweep} className="text-lg" />
                  Clear All History
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HistorySidebar;